import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { useEffect } from 'react'
import { useOnLogout } from '~/hooks/useOnLogout'
import { useGetUserSessionDependencies } from '~/queries/userSessionDependencyQueries'
import Logger from '~/services/Logger'
import { UserState } from '~/types/UserStateTypes'
import { initializePosthog } from '~/utils/initializePosthog'

import { ExtensionInstance, Pbx } from '@mangovoice/types'

import { StorageKeys } from './StorageKeys'

let refreshState: boolean = true

const userStateAtom = atomWithStorage<UserState>(
  StorageKeys.UserSessionData,
  {},
)
const logger = new Logger('UserState')
type UseUserStateProps = {
  canAutoRefresh: boolean
}
export const useUserState = (props?: UseUserStateProps) => {
  const [userState, setUserState] = useAtom(userStateAtom)

  const clearUserState = () => {
    setUserState({})
  }

  useOnLogout(() => {
    logger.info('User state cleared by useOnLogout')
    clearUserState()
  }, 'useUserState')

  //gets ext info, pbx, and all extensions for a user
  const {
    refetch: getUserSessionDependenciesInternal,
    isFetching: userStateLoading,
  } = useGetUserSessionDependencies({
    onQuerySuccess: (data) => {
      logger.info('Successfully fetched user session dependencies')

      //for backwards compatibility, if we have currentUserExtension, but no currentUserExtensions records, we add the currentUserExtension to the currentUserExtensions
      let currentUserExtensions = userState.currentUserExtensions
      if (
        !currentUserExtensions &&
        userState.currentUserExtension &&
        userState.currentUserExtension.pbx.id === data.pbx.id
      ) {
        currentUserExtensions = {
          [data.pbx.id]: userState.currentUserExtension,
        }
      }

      setUserState({
        pbxId: data.pbx.id,
        pbx: data.pbx,
        pbxUsers: data.pbxUsers,
        sessionUser: data.sessionUser,
        pbxExtensions: data.userExtensions,
        currentUserExtensions: currentUserExtensions,
        currentUserExtensionId: currentUserExtensions?.[data.pbx.id]?.id,
        currentUserExtension: currentUserExtensions?.[data.pbx.id],
        messagingEnabled: data.pbx.features.messaging_enabled,
        enhancedCallLogsEnabled: data.pbx.features.web_app_call_logs,
        callAnalyticsEnabled: data.pbx.features.call_analytics,
        agentStatusesEnabled: data.pbx.features.agent_statuses,
        userRoles: data.userRoles,
        isErrored: false,
      })
    },
    onQueryErrored: (extensionId, error) => {
      logger.error('Failed to fetch user session dependencies', null, {
        extensionId,
        error,
      })

      setUserState({
        isErrored: true,
      })
    },
  })

  const setCurrentUserExtension = (
    pbxId?: number,
    extension?: ExtensionInstance,
  ) => {
    if (!pbxId) {
      return
    }
    const currentUserExtensions = userState.currentUserExtensions || {}
    if (!extension) {
      delete currentUserExtensions[pbxId]
    } else {
      currentUserExtensions[pbxId] = extension
    }
    setUserState((prev) => ({
      ...prev,
      currentUserExtensions: currentUserExtensions,
      currentUserExtensionId: extension?.id,
      currentUserExtension: extension,
    }))
  }

  const getUserSessionDependencies = (pbx: Pbx) => {
    getUserSessionDependenciesInternal(pbx)
  }

  if (refreshState && props?.canAutoRefresh) {
    if (userState.pbx) {
      getUserSessionDependenciesInternal(userState.pbx)
      refreshState = false
    }
  }

  useEffect(() => {
    if (userState.pbx) {
      initializePosthog(userState.pbx.id, userState.sessionUser)
    }
  }, [userState.pbx, userState.sessionUser])

  return {
    ...userState,
    isReady: Boolean(userState.pbx),
    isLoading: userStateLoading,
    getUserSessionDependencies,
    setCurrentUserExtension,
  }
}
